import { useEffect, useRef } from "react";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { EXPLORE_ROUTES } from "@/features/dashboard/lib/explore-search";

const SEARCH_PERSIST_ROUTES = new Set(["/opportunities", ...EXPLORE_ROUTES]);

/** Records a tracked route's search string when the user leaves it, so nav links can restore it. */
export function usePersistRouteSearchOnLeave(pathname: string, href: string): void {
  const setLastRouteSearch = useAuth((s) => s.setLastRouteSearch);

  // Keep a ref to the latest search string for the current route. Updated
  // synchronously during render so the effect cleanup always sees the most
  // recent value without href appearing in the effect's dependency array.
  const lastSearchRef = useRef<string>("");
  if (SEARCH_PERSIST_ROUTES.has(pathname)) {
    const idx = href.indexOf("?");
    // Strip hash fragment so it doesn't bleed into the last param value
    lastSearchRef.current = idx >= 0 ? href.slice(idx + 1).split("#")[0] : "";
  }

  // Cleanup runs with the old pathname (closure) while the ref already holds
  // the final search string from the last render on that route.
  useEffect(() => {
    if (!SEARCH_PERSIST_ROUTES.has(pathname)) return;
    return () => {
      setLastRouteSearch(pathname, lastSearchRef.current);
    };
  }, [pathname, setLastRouteSearch]);
}
